// UserJourney.jsx - Complete Component with Lucide Icons
import React, { useState, useEffect } from 'react';
import {
  User,
  Clock,
  Smartphone,
  MapPin,
  Eye,
  ShoppingCart,
  CreditCard,
  Package,
  Search,
  FileText,
  Home,
  UserPlus,
  LogIn,
  Activity,
  Monitor,
  Calendar,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react';

const API_BASE = 'https://api.medstown.com/api/events';

const UserJourney = ({ userId: propUserId }) => {
  const [userId, setUserId] = useState(propUserId || '');
  const [range, setRange] = useState('thisWeek');
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [journeyData, setJourneyData] = useState(null);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (userId) {
      fetchUserJourney();
    }
  }, [userId, range, limit]);

  const fetchUserJourney = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({ range, limit: limit.toString() });
      const response = await fetch(`${API_BASE}/users/${userId}/journey?${params}`);
      const data = await response.json();
      setJourneyData(data.data || data);
    } catch (error) {
      console.error('Failed to fetch user journey:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEvent = (index) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedEvents(newExpanded);
  };

  const getEventIcon = (eventType, screen) => {
    const iconProps = { size: 20, strokeWidth: 2.5 };
    
    if (eventType === 'screen_view') {
      if (screen?.toLowerCase().includes('home')) return <Home {...iconProps} />;
      if (screen?.toLowerCase().includes('cart')) return <ShoppingCart {...iconProps} />;
      if (screen?.toLowerCase().includes('payment')) return <CreditCard {...iconProps} />;
      if (screen?.toLowerCase().includes('search')) return <Search {...iconProps} />;
      if (screen?.toLowerCase().includes('prescription')) return <FileText {...iconProps} />;
      if (screen?.toLowerCase().includes('product')) return <Package {...iconProps} />;
      if (screen?.toLowerCase().includes('login')) return <LogIn {...iconProps} />;
      if (screen?.toLowerCase().includes('register')) return <UserPlus {...iconProps} />;
      if (screen?.toLowerCase().includes('profile')) return <User {...iconProps} />;
      if (screen?.toLowerCase().includes('pet')) return <Package {...iconProps} />;
      if (screen?.toLowerCase().includes('otc')) return <Package {...iconProps} />;
      if (screen?.toLowerCase().includes('order')) return <Package {...iconProps} />;
      return <Monitor {...iconProps} />;
    }
    
    if (eventType === 'product_view') return <Eye {...iconProps} />;
    if (eventType === 'add_to_cart') return <ShoppingCart {...iconProps} />;
    if (eventType === 'payment_initiated') return <CreditCard {...iconProps} />;
    
    return <Activity {...iconProps} />;
  };

  const getEventColor = (eventType, screen) => {
    if (eventType === 'product_view') return '#9f7aea';
    if (eventType === 'add_to_cart') return '#ed8936';
    if (eventType === 'payment_initiated') return '#48bb78';
    
    if (screen?.toLowerCase().includes('home')) return '#667eea';
    if (screen?.toLowerCase().includes('cart')) return '#ed8936';
    if (screen?.toLowerCase().includes('payment')) return '#48bb78';
    if (screen?.toLowerCase().includes('search')) return '#38b2ac';
    
    return '#667eea';
  };

  const formatEventType = (eventType) => {
    return eventType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatScreen = (screen) => {
    if (!screen) return 'Unknown Screen';
    return screen
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '24px' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        
        {/* HEADER */}
        <div style={{ 
          backgroundColor: '#fff',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            marginBottom: '24px'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)'
            }}>
              <Activity size={32} color="#fff" />
            </div>
            <div>
              <h1 style={{ 
                fontSize: '32px', 
                fontWeight: '800', 
                color: '#1a202c',
                margin: '0 0 4px 0'
              }}>
                User Journey Analytics
              </h1>
              <p style={{ fontSize: '14px', color: '#718096', margin: 0 }}>
                Track individual user events and interactions
              </p>
            </div>
          </div>

          {/* USER ID INPUT */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr auto',
            gap: '12px',
            marginBottom: '16px'
          }}>
            <input
              type="text"
              placeholder="Enter User ID..."
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{
                padding: '14px 20px',
                fontSize: '14px',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                outline: 'none',
                transition: 'border-color 0.2s',
                fontFamily: 'monospace'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
            />
            <button
              onClick={fetchUserJourney}
              disabled={!userId || loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (!userId || loading) ? 'not-allowed' : 'pointer',
                opacity: (!userId || loading) ? 0.6 : 1,
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s'
              }}
            >
              {loading ? <RefreshCw size={16} className="spin" /> : <Search size={16} />}
              {loading ? 'Loading...' : 'Load Journey'}
            </button>
          </div>

          {/* FILTERS TOGGLE */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 16px',
              backgroundColor: '#f7fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '600',
              color: '#4a5568',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            {showFilters ? 'Hide' : 'Show'} Filters
          </button>

          {/* FILTERS */}
          {showFilters && (
            <div style={{ 
              marginTop: '16px',
              padding: '20px',
              backgroundColor: '#f7fafc',
              borderRadius: '12px',
              border: '1px solid #e2e8f0'
            }}>
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {/* Date Range */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Date Range
                  </label>
                  <select
                    value={range}
                    onChange={(e) => setRange(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="thisWeek">This Week</option>
                    <option value="lastWeek">Last Week</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                  </select>
                </div>

                {/* Limit */}
                <div>
                  <label style={{ 
                    display: 'block',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#4a5568',
                    marginBottom: '8px'
                  }}>
                    Event Limit
                  </label>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '13px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '10px',
                      backgroundColor: '#fff',
                      cursor: 'pointer',
                      outline: 'none'
                    }}
                  >
                    <option value="50">50 events</option>
                    <option value="100">100 events</option>
                    <option value="200">200 events</option>
                    <option value="500">500 events</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* USER INFO & STATS */}
        {journeyData && (
          <>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '20px',
              marginBottom: '32px'
            }}>
              {/* User Info Card */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '24px',
                    fontWeight: '800'
                  }}>
                    {journeyData.userInfo?.fullName?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#718096', fontWeight: '600' }}>
                      User Name
                    </div>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#1a202c' }}>
                      {journeyData.userInfo?.fullName || 'Anonymous'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#718096', marginTop: '2px' }}>
                      {journeyData.userInfo?.phone || 'No phone'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Events */}
              <StatCard
                icon={Activity}
                title="Total Events"
                value={journeyData.totalEvents || 0}
                color="#667eea"
              />

              {/* Unique Screens */}
              <StatCard
                icon={Monitor}
                title="Unique Screens"
                value={new Set(journeyData.events?.map(e => e.screen)).size || 0}
                color="#48bb78"
              />

              {/* Time Range */}
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <div style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: '#ed893620',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Calendar size={28} color="#ed8936" />
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#718096', fontWeight: '600' }}>
                      Time Range
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c' }}>
                      {range.replace(/([A-Z])/g, ' $1').trim()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* JOURNEY TIMELINE */}
            <div style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '32px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '32px',
                paddingBottom: '16px',
                borderBottom: '2px solid #f7fafc'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: '#667eea20',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Clock size={22} color="#667eea" />
                </div>
                <h2 style={{ 
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#1a202c',
                  margin: 0
                }}>
                  Event Timeline ({journeyData.totalEvents} events)
                </h2>
              </div>

              {/* Events List */}
              {!journeyData.events || journeyData.events.length === 0 ? (
                <div style={{ 
                  textAlign: 'center',
                  padding: '60px 20px',
                  color: '#a0aec0'
                }}>
                  <Activity size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                  <p style={{ fontSize: '16px', fontWeight: '600' }}>
                    No events found for this user
                  </p>
                </div>
              ) : (
                <div style={{ position: 'relative' }}>
                  {/* Timeline Line */}
                  <div style={{
                    position: 'absolute',
                    left: '27px',
                    top: '0',
                    bottom: '0',
                    width: '3px',
                    background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
                    opacity: 0.2
                  }} />

                  {/* Events */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    {journeyData.events.map((event, index) => {
                      const color = getEventColor(event.eventType, event.screen);
                      const isExpanded = expandedEvents.has(index);
                      
                      return (
                        <div
                          key={index}
                          style={{
                            position: 'relative',
                            paddingLeft: '68px',
                            cursor: 'pointer'
                          }}
                          onClick={() => toggleEvent(index)}
                        >
                          {/* Timeline Icon */}
                          <div style={{
                            position: 'absolute',
                            left: '0',
                            top: '0',
                            width: '56px',
                            height: '56px',
                            backgroundColor: `${color}20`,
                            border: `3px solid ${color}`,
                            borderRadius: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: color,
                            zIndex: 1,
                            boxShadow: `0 4px 12px ${color}30`
                          }}>
                            {getEventIcon(event.eventType, event.screen)}
                          </div>

                          {/* Event Card */}
                          <div style={{
                            backgroundColor: isExpanded ? `${color}05` : '#f7fafc',
                            border: `2px solid ${isExpanded ? `${color}40` : '#e2e8f0'}`,
                            borderRadius: '16px',
                            padding: '20px',
                            transition: 'all 0.3s'
                          }}>
                            {/* Event Header */}
                            <div style={{ 
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              marginBottom: isExpanded ? '16px' : '0'
                            }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  marginBottom: '8px'
                                }}>
                                  <span style={{
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    color: '#1a202c'
                                  }}>
                                    {formatEventType(event.eventType)}
                                  </span>
                                  {event.screen && (
                                    <>
                                      <ArrowRight size={16} color="#cbd5e0" />
                                      <span style={{
                                        fontSize: '15px',
                                        fontWeight: '600',
                                        color: '#4a5568',
                                        backgroundColor: `${color}15`,
                                        padding: '4px 12px',
                                        borderRadius: '8px'
                                      }}>
                                        {formatScreen(event.screen)}
                                      </span>
                                    </>
                                  )}
                                </div>
                                
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '16px',
                                  flexWrap: 'wrap'
                                }}>
                                  <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    color: '#718096'
                                  }}>
                                    <Clock size={14} />
                                    {new Date(event.timestamp).toLocaleString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      second: '2-digit'
                                    })}
                                  </div>
                                  
                                  {event.device && (
                                    <div style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontSize: '13px',
                                      color: '#718096'
                                    }}>
                                      <Smartphone size={14} />
                                      {event.device} • {event.os}
                                    </div>
                                  )}
                                  
                                  {event.pincode && (
                                    <div style={{ 
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      fontSize: '13px',
                                      color: '#718096'
                                    }}>
                                      <MapPin size={14} />
                                      {event.pincode}
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div style={{
                                padding: '8px',
                                borderRadius: '8px',
                                backgroundColor: isExpanded ? `${color}20` : 'transparent',
                                transition: 'all 0.2s'
                              }}>
                                {isExpanded ? <ChevronUp size={20} color={color} /> : <ChevronDown size={20} color="#cbd5e0" />}
                              </div>
                            </div>

                            {/* Expanded Details */}
                            {isExpanded && (
                              <div style={{
                                paddingTop: '16px',
                                borderTop: `1px solid ${color}20`,
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                gap: '12px'
                              }}>
                                {event.productId && (
                                  <DetailItem
                                    label="Product ID"
                                    value={event.productId}
                                    icon={Package}
                                    color={color}
                                  />
                                )}
                                {event.productName && (
                                  <DetailItem
                                    label="Product Name"
                                    value={event.productName}
                                    icon={Package}
                                    color={color}
                                  />
                                )}
                                {event.productType && (
                                  <DetailItem
                                    label="Product Type"
                                    value={event.productType}
                                    icon={Package}
                                    color={color}
                                  />
                                )}
                                {event.cartValue && (
                                  <DetailItem
                                    label="Cart Value"
                                    value={`₹${event.cartValue}`}
                                    icon={ShoppingCart}
                                    color={color}
                                  />
                                )}
                                {event.paymentAmount && (
                                  <DetailItem
                                    label="Payment Amount"
                                    value={`₹${event.paymentAmount}`}
                                    icon={CreditCard}
                                    color={color}
                                  />
                                )}
                                {event.eventIndex !== undefined && (
                                  <DetailItem
                                    label="Event Index"
                                    value={event.eventIndex}
                                    icon={Activity}
                                    color={color}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

const StatCard = ({ icon: Icon, title, value, color }) => (
  <div style={{
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid #e2e8f0',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
      <div style={{
        width: '56px',
        height: '56px',
        backgroundColor: `${color}20`,
        borderRadius: '12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Icon size={28} color={color} />
      </div>
      <div>
        <div style={{ fontSize: '13px', color: '#718096', fontWeight: '600' }}>
          {title}
        </div>
        <div style={{ fontSize: '28px', fontWeight: '800', color: '#1a202c' }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
      </div>
    </div>
  </div>
);

const DetailItem = ({ label, value, icon: Icon, color }) => (
  <div style={{
    backgroundColor: '#fff',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${color}20`
  }}>
    <div style={{ 
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginBottom: '4px'
    }}>
      <Icon size={14} color={color} />
      <span style={{ fontSize: '12px', color: '#718096', fontWeight: '600' }}>
        {label}
      </span>
    </div>
    <div style={{ 
      fontSize: '14px',
      fontWeight: '600',
      color: '#2d3748',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}>
      {value}
    </div>
  </div>
);

export default UserJourney;
